import React, {Fragment, useEffect, useState} from 'react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
}

interface InstallPWAButtonProps {
    onInstall: () => void;
    renderButton: (onClick: () => void, disabled: boolean) => JSX.Element;
}

const InstallPWAButton: React.FC<InstallPWAButtonProps> = ({ onInstall, renderButton }) => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const handler = (e: Event) => {
            const event = e as BeforeInstallPromptEvent;
            e.preventDefault();
            setDeferredPrompt(event);
        };

        window.addEventListener('beforeinstallprompt', handler);

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();

            setDeferredPrompt(null);
            onInstall();
        } else {
            // Fallback for Safari or unsupported browsers
            setShowModal(true);
        }
    };

    if (showModal) {
        return <Fragment>
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
                        <h2 className="text-3xl font-bold mb-4">📲 Install This App</h2>
                        <p className="text-lg mb-6">To install this app on your iPhone or iPad:</p>
                        <ol className="text-left text-base space-y-2 list-decimal list-inside mb-6">
                            <li>
                                Tap the <strong>Share</strong> icon{' '}
                                <span role="img" aria-label="share">🔗</span> in Safari.
                            </li>
                            <li>
                                Scroll down and tap <strong>“Add to Home Screen”</strong>.
                            </li>
                            <li>
                                Confirm by tapping <strong>Add</strong>.
                            </li>
                        </ol>
                        <button
                            className="text-white bg-[#202A43] text-lg font-semibold px-6 py-2 rounded-xl hover:bg-blue-700 transition"
                            onClick={() => setShowModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
        </Fragment>;
    }

    return renderButton(handleInstallClick, !deferredPrompt);
};

export default InstallPWAButton;

